import { Flex, Icon, Text } from '@chakra-ui/react'
import { Search, SearchX } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { ScrollingList } from '~/components/shared/ScrollingList'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { SearchValue } from '~/types/Shared'

import { ContactSourceType } from '@mangovoice/types'

import { useContactCardsListStyles } from './componentStyles/ContactCardsList.style'
import { ContactCard } from './ContactCard'
import { isPhoneNumber } from './utils/stringUtils'

interface ContactCardsListProps {
  isSearching?: boolean
  searchValue?: SearchValue
  searchContacts?: ContactDetails[]
  onSearchEndReached?: () => void
  isSearchLoading?: boolean
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
}
export const ContactCardsList = ({
  isSearching,
  searchValue,
  searchContacts,
  onSearchEndReached,
  isSearchLoading,
  onStartCall: handleStartCall,
}: ContactCardsListProps) => {
  const { t } = useTranslation()
  const styles = useContactCardsListStyles()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const emptySearchBodyProps = {
    flexDirection: styles.emptySearchBody?.flexDirection,
    justifyContent: styles.emptySearchBody?.justifyContent,
    alignItems: styles.emptySearchBody?.alignItems,
    gap: styles.emptySearchBody?.gap,
  }

  const emptySearchTextContainerProps = {
    flexDirection: styles.emptySearchTextContainer?.flexDirection,
    justifyContent: styles.emptySearchTextContainer?.justifyContent,
    alignItems: styles.emptySearchTextContainer?.alignItems,
  }

  const resultsHeaderProps = {
    flexDirection: styles.resultsHeader?.flexDirection,
    gap: styles.resultsHeader?.gap,
  }

  const emptySearchResultsBodyProps = {
    flexDirection: styles.emptySearchResultsBody?.flexDirection,
    alignItems: styles.emptySearchResultsBody?.alignItems,
    gap: styles.emptySearchResultsBody?.gap,
  }

  const searchResultsContainerProps = {
    justifyContent: styles.searchResultsContainer?.justifyContent,
    alignItems: styles.searchResultsContainer?.alignItems,
  }

  const getContactCardsListContent = () => {
    if (!isSearching) {
      return (
        <Flex css={styles.emptySearchBody} {...emptySearchBodyProps}>
          <Icon css={styles.emptySearchIcon}>
            <Search size="100%" />
          </Icon>
          <Flex
            css={styles.emptySearchTextContainer}
            {...emptySearchTextContainerProps}
          >
            <Text css={styles.emptySearchText}>
              {t('contactCardsList.emptySearchText')}
            </Text>
          </Flex>
        </Flex>
      )
    }
    if (!searchContacts || (!isSearchLoading && searchContacts.length === 0)) {
      const shouldShowContactCard =
        searchValue && isPhoneNumber(searchValue.value)
      return (
        <>
          <Flex css={styles.resultsHeader} {...resultsHeaderProps}>
            <Text css={styles.resultsHeaderText}>
              {t('contactCardList.searchResults')}
            </Text>
            <Text css={styles.emptySearchResultsText}>
              {t('contactCardList.noMatchingContacts')}
              <Text as="span">"{searchValue?.value}"</Text>
            </Text>
          </Flex>
          {shouldShowContactCard && (
            <ContactCard
              contact={{
                id: -1,
                name: '',
                defaultNumber: {
                  id: 0,
                  number: searchValue.value,
                  label: 'other',
                  isPrimary: false,
                  isExtension: false,
                  availableActions: [],
                  availableQuickActions: [],
                },
                numbers: [],
                source: ContactSourceType.Unknown,
                sourceName: t('webPhoneExtras.failedSearch', 'Failed Search'),
              }}
              onStartCall={handleStartCall}
            />
          )}
          {!shouldShowContactCard && (
            <Flex
              css={styles.emptySearchResultsBody}
              {...emptySearchResultsBodyProps}
            >
              <Icon css={styles.emptySearchResultsIcon}>
                <SearchX size="100%" />
              </Icon>
            </Flex>
          )}
        </>
      )
    } else if (isSearchLoading && searchContacts.length === 0) {
      return (
        <>
          <Flex css={styles.resultsHeader} {...resultsHeaderProps}>
            <Text css={styles.resultsHeaderText}>
              {t('contactCardList.searchResults')}
            </Text>
          </Flex>
          <Flex
            css={styles.searchResultsContainer}
            {...searchResultsContainerProps}
          >
            <LoadingSpinner />
          </Flex>
        </>
      )
    } else {
      return (
        <>
          <Flex css={styles.resultsHeader} {...resultsHeaderProps}>
            <Text css={styles.resultsHeaderText}>
              {t('contactCardList.searchResults')}
            </Text>
          </Flex>
          <ScrollingList
            display="flex"
            width="full"
            height="full"
            minHeight="0"
            onEndReached={onSearchEndReached}
            showLoaderFooter={isSearchLoading}
            scrollbarOptions={{ showOnHoverOnly: true }}
            itemData={searchContacts}
            onBuildItemContent={(_, contact) => {
              return (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onStartCall={handleStartCall}
                />
              )
            }}
          />
        </>
      )
    }
  }

  return (
    <Flex
      flexDirection="column"
      css={{
        height: 'calc(100vh - 400px)',
        overflow: 'scroll',
      }}
    >
      {getContactCardsListContent()}
    </Flex>
  )
}
